import React, { useState, useEffect } from 'react';
import './HomePage.css';
import {
    Flex,
    Card,
    Text,
    Button,
    Heading,
    Avatar,
    Badge,
    Separator
} from '@radix-ui/themes';
import {
    ExitIcon,
    PersonIcon,
    StarIcon,
    CalendarIcon,
    CameraIcon,
    EnvelopeClosedIcon,
    MobileIcon,
    InfoCircledIcon,
    ExclamationTriangleIcon,
    CheckCircledIcon,
    CrossCircledIcon
} from '@radix-ui/react-icons';
import AppBar from './components/AppBar';
import NavigationDrawer from './components/NavigationDrawer';
import { apiService, Member, API_BASE_URL, FILE_SERVER_URL, MemberProfile, DojoDetails, Announcement } from './services/api';
import * as Dialog from '@radix-ui/react-dialog';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';

interface HomePageProps {
    onLogout: () => void;
    students: Member[];
    selectedStudent: Member | null;
    onStudentChange: (student: Member) => void;
}

const HomePage: React.FC<HomePageProps> = ({ onLogout, students, selectedStudent, onStudentChange }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const [profile, setProfile] = useState<MemberProfile | null>(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [profileError, setProfileError] = useState<string | null>(null);
    const [dojoDetails, setDojoDetails] = useState<DojoDetails | null>(null);
    const [dojoLoading, setDojoLoading] = useState(false);
    const [dojoError, setDojoError] = useState<string | null>(null);
    const [announcements, setAnnouncements] = useState<Announcement[]>([]);
    const [announcementsLoading, setAnnouncementsLoading] = useState(false);
    const [announcementsError, setAnnouncementsError] = useState<string | null>(null);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<number>>(new Set());
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Custom hook for responsive screen size detection
    const useResponsiveSize = () => {
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

        useEffect(() => {
            const handleResize = () => {
                setIsMobile(window.innerWidth <= 768);
            };

            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);

        return isMobile;
    };

    const isMobile = useResponsiveSize();

    useEffect(() => {
        if (!selectedStudent) {
            console.log("[Announcements] No selectedStudent, announcements API will not be called.");
            return;
        }
        console.log("[Announcements] Calling announcements API for memberId:", selectedStudent.memberId);

        setAnnouncementsLoading(true);
        setAnnouncementsError(null);
        apiService.getAnnouncements(selectedStudent.memberId)
            .then(res => {
                console.log("[Announcements] API response:", res);
                if (res.success) {
                    setAnnouncements(res.announcements || []);
                } else {
                    setAnnouncementsError('Failed to fetch announcements 1');
                }
            })
            .catch((err) => {
                setAnnouncementsError('Failed to fetch announcements 2');
                console.error("[Announcements] API error:", err);
            })
            .finally(() => setAnnouncementsLoading(false));
    }, [selectedStudent]);

    useEffect(() => {
        if (!selectedStudent) return;

        // Fetch profile details
        setProfileLoading(true);
        setProfileError(null);
        apiService.getMemberProfile(selectedStudent.memberId)
            .then(res => {
                if (res.success) {
                    setProfile(res.member);
                } else {
                    setProfileError('Failed to fetch profile');
                }
            })
            .catch(() => setProfileError('Failed to fetch profile'))
            .finally(() => setProfileLoading(false));

        // Fetch dojo details
        setDojoLoading(true);
        setDojoError(null);
        apiService.getDojoDetails(selectedStudent.memberId)
            .then(res => {
                if (res.success) {
                    setDojoDetails(res.data);
                } else {
                    setDojoError('Failed to fetch dojo details');
                }
            })
            .catch(() => setDojoError('Failed to fetch dojo details'))
            .finally(() => setDojoLoading(false));

        // Fetch announcements
        setAnnouncementsLoading(true);
        setAnnouncementsError(null);
        apiService.getAnnouncements(selectedStudent.memberId)
            .then(res => {
                if (res.success) {
                    setAnnouncements(res.announcements || []);
                } else {
                    setAnnouncementsError('Failed to fetch announcements 3');
                }
            })
            .catch(() => setAnnouncementsError('Failed to fetch announcements 4'))
            .finally(() => setAnnouncementsLoading(false));
    }, [selectedStudent]);

    const handleLogout = async () => {
        try {
            await signOut(auth);
        } catch (e) {
            // Optionally log error, but proceed to clear tokens
        }
        apiService.removeToken();
        onLogout();
    };

    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Validate file size (2MB limit)
            if (file.size > 2 * 1024 * 1024) {
                setError('File must be <= 2MB');
                setTimeout(() => setError(null), 3000);
                return;
            }

            // Validate file type
            if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
                setError('File must be JPG/PNG');
                setTimeout(() => setError(null), 3000);
                return;
            }

            const reader = new FileReader();
            reader.onload = () => {
                if (typeof reader.result === 'string') {
                    setSelectedImage(file);
                    setImageUrl(reader.result);
                    setIsCropDialogOpen(true);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const onCropComplete = (_: Area, croppedAreaPixels: Area) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    async function getCroppedImg(imageSrc: string, crop: Area) {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No 2d context');

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = crop.width;
        canvas.height = crop.height;

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );

        return new Promise<Blob>((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
            }, 'image/jpeg');
        });
    }

    function createImage(url: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const image = new window.Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.setAttribute('crossOrigin', 'anonymous');
            image.src = url;
        });
    }

    const handleUpload = async () => {
        if (!selectedStudent || !imageUrl || !croppedAreaPixels || !selectedImage) return;
        setUploading(true);
        try {
            const croppedBlob = await getCroppedImg(imageUrl, croppedAreaPixels);
            const formData = new FormData();
            formData.append('k_id', selectedStudent.memberId.toString());
            formData.append('file', croppedBlob, 'avatar.jpg');

            const token = apiService.getToken();
            if (!token) {
                setError('No authentication token found');
                setTimeout(() => setError(null), 3000);
                return;
            }

            const response = await fetch(`${API_BASE_URL}/v2/uploading-file-api`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            const data = await response.json();
            if (data.success) {
                // Use the file value from API response to update the selected student's avatar
                const updatedStudent = {
                    ...selectedStudent,
                    avatar: data.file // Use the new filename from the API response
                };
                onStudentChange(updatedStudent);

                setSuccessMessage('Profile picture updated!');
                setTimeout(() => setSuccessMessage(null), 3000);
                setIsCropDialogOpen(false);
            } else {
                setError(data.message || 'Upload failed');
                setTimeout(() => setError(null), 3000);
            }
        } catch (err) {
            setError('Upload failed');
            setTimeout(() => setError(null), 3000);
        } finally {
            setUploading(false);
            setSelectedImage(null);
            setImageUrl(null);
        }
    };

    const handleCloseDialog = () => {
        setIsCropDialogOpen(false);
        setSelectedImage(null);
        setImageUrl(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
    };

    const calculateAge = (dateOfBirth: string) => {
        if (!dateOfBirth) return 'N/A';

        try {
            // Parse DD-MM-YYYY format
            const parts = dateOfBirth.split('-');
            if (parts.length !== 3) return 'N/A';

            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date constructor
            const year = parseInt(parts[2], 10);

            const dob = new Date(year, month, day);
            const today = new Date();

            let age = today.getFullYear() - dob.getFullYear();
            const monthDiff = today.getMonth() - dob.getMonth();

            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
                age--;
            }

            return age.toString();
        } catch (error) {
            return 'N/A';
        }
    };

    const formatClassDays = (classDays: string) => {
        if (!classDays) return 'N/A';

        const dayMap: { [key: string]: string } = {
            '1': 'Monday',
            '2': 'Tuesday',
            '3': 'Wednesday',
            '4': 'Thursday',
            '5': 'Friday',
            '6': 'Saturday',
            '7': 'Sunday'
        };

        const days = classDays.split(',').map(day => day.trim());
        const dayNames = days.map(day => dayMap[day]).filter(Boolean);

        return dayNames.join(', ');
    };

    // Announcements helper functions
    const getAnnouncementIcon = (type: Announcement['type']) => {
        switch (type) {
            case 'info': return <InfoCircledIcon width="16" height="16" />;
            case 'warning': return <ExclamationTriangleIcon width="16" height="16" />;
            case 'success': return <CheckCircledIcon width="16" height="16" />;
            case 'urgent': return <CrossCircledIcon width="16" height="16" />;
            default: return <InfoCircledIcon width="16" height="16" />;
        }
    };

    const getAnnouncementColor = (priority: Announcement['priority']) => {
        switch (priority) {
            case 'critical': return 'var(--red-9)';
            case 'high': return 'var(--orange-9)';
            case 'medium': return 'var(--blue-9)';
            case 'low': return 'var(--green-9)';
            default: return 'var(--gray-9)';
        }
    };

    const getAnnouncementBackground = (priority: Announcement['priority']) => {
        switch (priority) {
            case 'critical': return 'var(--red-2)';
            case 'high': return 'var(--orange-2)';
            case 'medium': return 'var(--blue-2)';
            case 'low': return 'var(--green-2)';
            default: return 'var(--gray-2)';
        }
    };

    const dismissAnnouncement = (id: number) => {
        setDismissedAnnouncements(prev => new Set(Array.from(prev).concat([id])));
    };

    const activeAnnouncements = announcements.filter(announcement =>
        announcement.isActive &&
        !dismissedAnnouncements.has(announcement.id) &&
        (!announcement.expiryDate || new Date(announcement.expiryDate) > new Date())
    );

    if (loading) {
        return (
            <div className="home-page">
                <div className="loading-container">
                    <Text size="4" weight="bold">Loading students...</Text>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="home-page">
                <div className="error-container">
                    <Text size="4" color="red" weight="bold">{error}</Text>
                    <Button onClick={() => window.location.reload()}>Retry</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="home-page">
            <AppBar
                onMenuClick={() => setIsDrawerOpen(true)}
                students={students}
                selectedStudent={selectedStudent}
                onStudentChange={onStudentChange}
            />

            <NavigationDrawer
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                onLogout={handleLogout}
                selectedStudent={selectedStudent}
            />

            <main className="home-content">
                <div className="content-container">
                    {/* Announcements Banner */}
                    {announcementsError && (
                        <div style={{ marginBottom: '8px' }}>
                            <Text color="red">Announcements Error: {announcementsError}</Text>
                        </div>
                    )}
                    {activeAnnouncements.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                            {activeAnnouncements.map((announcement) => (
                                <Card
                                    key={announcement.id}
                                    style={{
                                        backgroundColor: getAnnouncementBackground(announcement.priority),
                                        border: `1px solid ${getAnnouncementColor(announcement.priority)}`,
                                        borderRadius: '8px',
                                        marginBottom: '8px',
                                        position: 'relative'
                                    }}
                                >
                                    <Flex align="center" gap="3" justify="between">
                                        <Flex align="center" gap="2">
                                            <div style={{ color: getAnnouncementColor(announcement.priority) }}>
                                                {getAnnouncementIcon(announcement.type)}
                                            </div>
                                            <Flex direction="column" gap="1">
                                                <Text size="3" weight="bold" style={{ color: getAnnouncementColor(announcement.priority) }}>
                                                    {announcement.title}
                                                </Text>
                                                <Text size="2" color="gray">
                                                    {announcement.content}
                                                </Text>
                                                <Text size="1" color="gray">
                                                    By {announcement.author} • {new Date(announcement.publishDate).toLocaleDateString()}
                                                </Text>
                                            </Flex>
                                        </Flex>
                                        <button
                                            onClick={() => dismissAnnouncement(announcement.id)}
                                            style={{
                                                backgroundColor: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '4px',
                                                borderRadius: '4px',
                                                color: getAnnouncementColor(announcement.priority),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                        >
                                            <Text size="2" weight="bold">×</Text>
                                        </button>
                                    </Flex>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Welcome/Profile Section */}
                    <Card className="welcome-card">
                        <Flex direction="column" gap="4">
                            <Flex align="center" gap="3">
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    <Avatar
                                        size={isMobile ? "7" : "9"}
                                        src={selectedStudent?.avatar && selectedStudent?.avatar !== 'NoImageFound.png' ? `${FILE_SERVER_URL}/show_files/${selectedStudent.avatar}` : undefined}
                                        fallback={selectedStudent?.fullName?.charAt(0) || 'S'}
                                        className="welcome-avatar"
                                    />
                                    <button
                                        type="button"
                                        className="avatar-upload-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{
                                            position: 'absolute',
                                            bottom: 0,
                                            right: 0,
                                            background: 'rgba(255,255,255,0.8)',
                                            border: 'none',
                                            borderRadius: '50%',
                                            padding: 4,
                                            cursor: 'pointer',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                        }}
                                        aria-label="Change profile picture"
                                    >
                                        <CameraIcon width={20} height={20} />
                                    </button>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/jpg"
                                        style={{ display: 'none' }}
                                        ref={fileInputRef}
                                        onChange={onSelectFile}
                                    />
                                </div>
                                <Dialog.Root open={isCropDialogOpen} onOpenChange={setIsCropDialogOpen}>
                                    <Dialog.Portal>
                                        <Dialog.Overlay className="DialogOverlay" />
                                        <Dialog.Content className="DialogContent">
                                            <Dialog.Title>Update Profile Photo</Dialog.Title>
                                            <Text size="2" color="gray" style={{ marginTop: 4 }}>
                                                Drag to crop. Only JPG/PNG, max 2MB.
                                            </Text>
                                            {imageUrl && (
                                                <div style={{
                                                    position: 'relative',
                                                    width: 250,
                                                    height: 250,
                                                    background: '#f5f5f5',
                                                    borderRadius: 8,
                                                    border: '1px solid #e0e0e0',
                                                    margin: '16px auto',
                                                    overflow: 'hidden'
                                                }}>
                                                    <Cropper
                                                        image={imageUrl}
                                                        crop={crop}
                                                        zoom={zoom}
                                                        aspect={1}
                                                        onCropChange={setCrop}
                                                        onZoomChange={setZoom}
                                                        onCropComplete={onCropComplete}
                                                    />
                                                </div>
                                            )}
                                            <div style={{ margin: '16px 0' }}>
                                                <Text size="2" weight="bold" style={{ marginBottom: 8 }}>Zoom</Text>
                                                <input
                                                    type="range"
                                                    min="1"
                                                    max="3"
                                                    step="0.01"
                                                    value={zoom}
                                                    onChange={(e) => setZoom(Number(e.target.value))}
                                                    className="zoom-slider"
                                                />
                                            </div>
                                            <Separator size="2" style={{ margin: '16px 0' }} />
                                            <Flex gap="4" justify="end" style={{ marginTop: 16 }}>
                                                <Button
                                                    variant="soft"
                                                    onClick={handleCloseDialog}
                                                    disabled={uploading}
                                                    style={{ minWidth: 80 }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="solid"
                                                    onClick={handleUpload}
                                                    disabled={uploading}
                                                    style={{ minWidth: 100 }}
                                                >
                                                    {uploading ? 'Uploading...' : 'Upload'}
                                                </Button>
                                            </Flex>
                                        </Dialog.Content>
                                    </Dialog.Portal>
                                </Dialog.Root>
                                <Flex direction="column" gap="1">
                                    <Heading size="8" className="welcome-title">
                                        Welcome back!
                                    </Heading>
                                    <Text size="3" color="gray" className="welcome-subtitle">
                                        {selectedStudent?.fullName}
                                    </Text>
                                    {profileLoading ? (
                                        <Text size="2" color="gray">Loading profile...</Text>
                                    ) : profileError ? (
                                        <Text size="2" color="red">{profileError}</Text>
                                    ) : null}
                                </Flex>
                            </Flex>
                        </Flex>
                    </Card>

                    {/* Profile Information */}
                    {profile && (
                        <div className="profile-stats-grid">
                            <Card className="stat-card">
                                <Flex direction="column" gap="2" align="center">
                                    <Text size="6" weight="bold" color="blue">
                                        {profile.rank_title}
                                    </Text>
                                    <Text size="2" color="gray">Rank</Text>
                                </Flex>
                            </Card>

                            <Card className="stat-card">
                                <Flex direction="column" gap="2" align="center">
                                    <Text size="6" weight="bold" color={profile.membershipStatus === 'EXPIRED' ? 'red' : 'green'}>
                                        {profile.membershipStatus}
                                    </Text>
                                    <Text size="2" color="gray">Membership Status</Text>
                                </Flex>
                            </Card>

                            <Card className="stat-card">
                                <Flex direction="column" gap="2" align="center">
                                    <Text size="6" weight="bold" color="orange">
                                        {profile.grNumber}
                                    </Text>
                                    <Text size="2" color="gray">GR Number</Text>
                                </Flex>
                            </Card>

                            <Card className="stat-card">
                                <Flex direction="column" gap="2" align="center">
                                    <Text size="6" weight="bold" color="purple">
                                        {profile.contactNumber}
                                    </Text>
                                    <Text size="2" color="gray">Contact</Text>
                                </Flex>
                            </Card>

                            <Card className="stat-card">
                                <Flex direction="column" gap="2" align="center">
                                    <Text size="6" weight="bold" color="teal">
                                        {profile.dateOfBirth}
                                    </Text>
                                    <Text size="2" color="gray">Birthdate</Text>
                                </Flex>
                            </Card>

                            <Card className="stat-card">
                                <Flex direction="column" gap="2" align="center">
                                    <Text size="6" weight="bold" color="indigo">
                                        {calculateAge(profile.dateOfBirth)}
                                    </Text>
                                    <Text size="2" color="gray">Age</Text>
                                </Flex>
                            </Card>
                        </div>
                    )}

                    {/* Student Stats */}
                    {/* <div className="stats-grid">
                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="blue">
                                    12
                                </Text>
                                <Text size="2" color="gray">Classes This Month</Text>
                            </Flex>
                        </Card>

                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="green">
                                    85%
                                </Text>
                                <Text size="2" color="gray">Attendance Rate</Text>
                            </Flex>
                        </Card>

                        <Card className="stat-card">
                            <Flex direction="column" gap="2" align="center">
                                <Text size="6" weight="bold" color="orange">
                                    3
                                </Text>
                                <Text size="2" color="gray">Belt Promotions</Text>
                            </Flex>
                        </Card>
                    </div> */}

                    {/* Coach & Dojo Details */}
                    <Card className="activity-card">
                        <Heading size="4" mb="4">Coach & Dojo Details</Heading>
                        {dojoLoading ? (
                            <Text size="3">Loading dojo details...</Text>
                        ) : dojoError ? (
                            <Text size="3" color="red">{dojoError}</Text>
                        ) : dojoDetails ? (
                            <div className="coach-details-grid">
                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <PersonIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Coach</Text>
                                        <Text size="1" color="gray">{dojoDetails.coachName}</Text>
                                    </Flex>
                                </Flex>

                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <MobileIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Phone</Text>
                                        <Text size="1" color="gray">
                                            <a
                                                href={`tel:${dojoDetails.coachContact}`}
                                                style={{
                                                    color: 'inherit',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {dojoDetails.coachContact}
                                            </a>
                                        </Text>
                                    </Flex>
                                    <a
                                        href={`tel:${dojoDetails.coachContact}`}
                                        style={{
                                            color: 'inherit',
                                            textDecoration: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginLeft: 'auto'
                                        }}
                                    >
                                        <MobileIcon width="16" height="16" className="activity-time" />
                                    </a>
                                </Flex>

                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <EnvelopeClosedIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Email</Text>
                                        <Text size="1" color="gray">
                                            <a
                                                href={`mailto:${dojoDetails.coachEmail}`}
                                                style={{
                                                    color: 'inherit',
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                {dojoDetails.coachEmail}
                                            </a>
                                        </Text>
                                    </Flex>
                                    <a
                                        href={`mailto:${dojoDetails.coachEmail}`}
                                        style={{
                                            color: 'inherit',
                                            textDecoration: 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            marginLeft: 'auto'
                                        }}
                                    >
                                        <EnvelopeClosedIcon width="16" height="16" className="activity-time" />
                                    </a>
                                </Flex>

                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <CalendarIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Location</Text>
                                        <Text size="1" color="gray">
                                            {dojoDetails.dojoName ? dojoDetails.dojoName : dojoDetails.schoolName || 'N/A'}
                                        </Text>
                                    </Flex>
                                </Flex>

                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <StarIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Address</Text>
                                        <Text size="1" color="gray">{dojoDetails.dojoAddress}</Text>
                                    </Flex>
                                </Flex>

                                <Flex align="center" gap="3" className="activity-item">
                                    <div className="activity-icon">
                                        <CalendarIcon width="16" height="16" />
                                    </div>
                                    <Flex direction="column" gap="1">
                                        <Text size="2" weight="bold">Class Schedule</Text>
                                        <Text size="1" color="gray">
                                            {dojoDetails.dojoFromTime} - {dojoDetails.dojoToTime} ({dojoDetails.dojoDuration} min) on ({formatClassDays(dojoDetails.dojoClassDays)})
                                        </Text>
                                    </Flex>
                                </Flex>


                            </div>
                        ) : null}
                    </Card>

                    {/* Quick Actions */}
                    <Card className="actions-card">
                        <Heading size="4" mb="4">Quick Actions</Heading>
                        <div className="actions-grid">
                            <Button className="action-button" variant="soft">
                                <CalendarIcon width="16" height="16" />
                                View Schedule
                            </Button>
                            <Button className="action-button" variant="soft">
                                <PersonIcon width="16" height="16" />
                                Profile
                            </Button>
                            <Button className="action-button" variant="soft">
                                <StarIcon width="16" height="16" />
                                Progress
                            </Button>
                        </div>
                    </Card>
                </div>
            </main>

            {/* Success Notification */}
            {successMessage && (
                <div className="notification notification-success">
                    {successMessage}
                </div>
            )}

            {/* Error Notification */}
            {error && (
                <div className="notification notification-error">
                    {error}
                </div>
            )}

        </div>
    );
};

export default HomePage; 